import React, { useCallback, useContext } from 'react';
import { motion, Variants } from 'framer-motion';
import styled from 'styled-components';
import _ from 'lodash';

import { SideBarStateContext } from './SideBarStateProvider';
import IconDiv from '../../../../../screens/V3/Workspace/screens/AllAssetsScreen/IconDiv';
import Icon from '../../../../../kingpin/atoms/Icon';
import SideBarStateButton from './SideBarStateButton';
import { SIDEBAR_ANIMATION_TIME } from './constants';
import { REACT_PORTAL_CHILDREN } from '../../../../../components/ReactPortal';

const COLLAPSED_WIDTH = 32;
const OPEN_WIDTH = 260;

const ExpandedSideBarDiv = styled(motion.div)`
  position: relative;
  height: calc(100vh - 58px);
  display: flex;
  flex-direction: column;
  overflow: visible;
  background-color: var(--color-bg-secondary);
  border-right: 1px solid var(--color-bg-sunken);
  .showPinOnMouseOver {
    visibility: hidden;
  }

  &::before {
    content: '';
    position: absolute;
    z-index: 1;
    // Only add threshold to div when hovered
  }

  &:hover {
    .showPinOnMouseOver {
      visibility: visible;
    }
  }
`;

const ExpandIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  margin-top: -48px;
`;

const variants: Variants = {
  opened: {
    width: OPEN_WIDTH,
    minWidth: `${OPEN_WIDTH}px`,
    transition: {
      duration: SIDEBAR_ANIMATION_TIME,
    },
  },
  closed: {
    width: COLLAPSED_WIDTH,
    minWidth: `${COLLAPSED_WIDTH}px`,
    transition: {
      duration: SIDEBAR_ANIMATION_TIME,
    },
  },
};

const PinnedSideBar = ({ children }: { children: React.ReactNode }) => {
  const { aniState, setAniState } = useContext(SideBarStateContext);

  const isFromPortal = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.relatedTarget;
    if (target instanceof HTMLElement) {
      const isFromPortal = target.closest(`#${REACT_PORTAL_CHILDREN}`);

      return isFromPortal;
    }

    return false;
  }, []);

  const onMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (isFromPortal(e)) {
        return;
      }

      setAniState((c) => {
        if (c.isHovered) {
          return c;
        }

        if (c.isPinning) {
          return c;
        }

        return { ...c, isHovered: true };
      });
    },
    [isFromPortal, setAniState],
  );
  const onMouseLeave = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (isFromPortal(e)) {
        return;
      }

      setAniState((current) => {
        if (!current.isOpen) {
          return current;
        }

        if (!current.isHovered) {
          return current;
        }

        return {
          ...current,
          isHovered: false,
        };
      });
    },
    [isFromPortal, setAniState],
  );

  return (
    <ExpandedSideBarDiv
      animate={aniState.isOpen ? 'opened' : 'closed'}
      initial={aniState.isOpen ? 'opened' : 'closed'}
      variants={variants}
      onAnimationStart={(anim) => {
        setAniState((c) => ({ ...c, isPinning: true }));
      }}
      onAnimationComplete={(anim) => {
        setAniState((c) => ({ ...c, isPinning: false }));
      }}
      onMouseEnter={_.debounce(onMouseEnter, 150)}
      onMouseLeave={_.debounce(onMouseLeave, 150)}
    >
      <div
        style={{
          display: aniState.isOpen && !aniState.isPinning ? 'flex' : 'none',
          flexDirection: 'column',
          flex: 1,
          overflow: 'hidden',
        }}
      >
        {children}
      </div>
      {!aniState.isOpen && (
        <ExpandIndicator>
          <IconDiv>
            <Icon
              color={'var(--color-text-tertiary)'}
              width={24}
              height={24}
              icon={'chevron-right'}
            ></Icon>
          </IconDiv>
        </ExpandIndicator>
      )}
      {aniState.isOpen && <SideBarStateButton />}
    </ExpandedSideBarDiv>
  );
};

export default PinnedSideBar;
